import { AUDIT_VIEW_ACTION } from '../filterRules';
import { isRecord } from '../mappers';
import { type AuditText, auditMessage, joinAuditTexts } from '../text';
import type { AuditLogEntry } from '../types';

import { listLength, numberField, textField } from './lines';
import type { AuditSummary } from './types';

type FactRule = (metadata: Record<string, unknown>) => AuditText | null;

const LOGIN_METHODS: ReadonlyMap<string, string> = new Map([
    ['password', 'auditLog.summary.loginPassword'],
    ['microsoft', 'auditLog.summary.loginMicrosoft'],
]);

// The list call's own paging params are not filters.
const VIEW_PAGING_PARAMS = new Set(['cursor', 'limit']);

function loginMethod(metadata: Record<string, unknown>): AuditText | null {
    const key = LOGIN_METHODS.get(textField(metadata, 'method') ?? '');
    return key === undefined ? null : auditMessage(key);
}

function createdPens(metadata: Record<string, unknown>): AuditText | null {
    const raw = metadata.names;
    const names = Array.isArray(raw)
        ? raw.filter((name): name is string => typeof name === 'string')
        : [];
    const count = names.length > 0 ? names.length : listLength(metadata, 'cageIds');
    if (count === null || count === 0) return null;
    return names.length > 0
        ? auditMessage('auditLog.summary.createdPensNamed', { count, names: joinAuditTexts(names) })
        : auditMessage('auditLog.summary.createdPens', { count });
}

function operate(metadata: Record<string, unknown>): AuditText | null {
    const operationType = textField(metadata, 'operationType');
    if (operationType === null) return null;
    return auditMessage('auditLog.summary.fishGroupOperate', {
        operationType,
        from: listLength(metadata, 'fishGroupIds') ?? 0,
        to: listLength(metadata, 'destinationFishGroupIds') ?? 0,
        count: listLength(metadata, 'cageIds') ?? 0,
    });
}

function revert(metadata: Record<string, unknown>): AuditText | null {
    const operationType = textField(metadata, 'revertedOperationType');
    if (operationType === null) return null;
    return auditMessage('auditLog.summary.fishGroupRevert', {
        operationType,
        restored: listLength(metadata, 'restoredFishGroupIds') ?? 0,
        discarded: listLength(metadata, 'discardedFishGroupIds') ?? 0,
    });
}

function event(metadata: Record<string, unknown>): AuditText | null {
    const eventType = textField(metadata, 'eventType');
    const before = numberField(metadata, 'fishCountBefore');
    const after = numberField(metadata, 'fishCountAfter');
    if (eventType === null) return null;
    return before === null || after === null
        ? eventType
        : auditMessage('auditLog.summary.fishGroupEvent', { eventType, before, after });
}

function groupCount(key: string, field: string): FactRule {
    return (metadata) => {
        const count = listLength(metadata, field);
        return count === null ? null : auditMessage(key, { count });
    };
}

function accessLevel(metadata: Record<string, unknown>): AuditText | null {
    const level = numberField(metadata, 'accessLevelId');
    return level === null ? null : auditMessage('auditLog.summary.apiUserAccessLevel', { level });
}

function exported(metadata: Record<string, unknown>): AuditText | null {
    const rows = numberField(metadata, 'rows');
    return rows === null ? null : auditMessage('auditLog.summary.exported', { count: rows });
}

/** The page sends this on every default view, so it is not a filter the reviewer chose. */
function isDefaultViewExclusion(key: string, value: unknown): boolean {
    if (key !== 'excludeActions') return false;
    const values = Array.isArray(value) ? value : [value];
    return values.length === 1 && values[0] === AUDIT_VIEW_ACTION;
}

function viewFilters(metadata: Record<string, unknown>): AuditText | null {
    const query = metadata.query;
    if (!isRecord(query)) return null;
    const count = Object.entries(query).filter(
        ([key, value]) => !VIEW_PAGING_PARAMS.has(key) && !isDefaultViewExclusion(key, value)
    ).length;
    return count === 0
        ? auditMessage('auditLog.summary.viewNoFilters')
        : auditMessage('auditLog.summary.viewFilters', { count });
}

/**
 * Service-written facts, keyed by action. Dotted action names cannot be i18n keys, since
 * `bioscope.move` would have to be a string and the parent of `bioscope.move.undo`.
 */
const FACTS: ReadonlyMap<string, FactRule> = new Map([
    ['auth.login', loginMethod],
    ['cage.create_many', createdPens],
    ['fish_group.operate', operate],
    ['fish_group.revert', revert],
    ['fish_group.event.create', event],
    ['fish_group.split', groupCount('auditLog.summary.fishGroupSplit', 'childFishGroupIds')],
    ['fish_group.merge', groupCount('auditLog.summary.fishGroupMerge', 'sourceFishGroupIds')],
    ['api_user.create', accessLevel],
    ['audit_log.export', exported],
    ['audit_log.view', viewFilters],
]);

/** Rule 6: what a service recorded about the action in its metadata. */
export function summarizeFacts(entry: AuditLogEntry): AuditSummary | null {
    const rule = FACTS.get(entry.action);
    const line = rule === undefined || entry.metadata === null ? null : rule(entry.metadata);
    return line === null ? null : { rule: 'fact', line, title: [line] };
}
